import axios from 'axios'
import DateFormat from '../shared/helpers/date-format'
import CancellableRequest from './cancellable-request'
import { DateTime } from 'luxon'

const { dashed } = DateFormat

const retrieveToken = () => {
  return document.querySelector('meta[name=csrf-token]')?.content
}
const api = axios.create({
  baseURL: '/',
  headers: {
    Accept: 'application/json',
    'X-Requested-With': 'XMLHttpRequest',
    'X-CSRF-Token': retrieveToken()
  }
})

const cancellable = new CancellableRequest(api)

const abortableApiCall = async (action, abortSignal, url, queryParams) => {
  const { data } = await api[action](url, queryParams, {
    signal: abortSignal
  })
  return data
}

export const safeApiCall = async (
  action,
  url,
  errorMessage = 'There was an error, please try again',
  reqData = null
) => {
  try {
    const { data } = await api[action](url, reqData)
    return data
  } catch (e) {
    console.warn(e)
    let error = new Error(errorMessage)
    error.code = 'api'
    error.status_code = e.response.status
    throw error
  }
}

export const getEvents = (date, productId = null) => {
  if (!date) return {}
  if (productId) {
    return safeApiCall(
      'get',
      `/api/products/${productId}?date=${dashed(date)}`,
      'Sorry, we could not find events for this date.'
    )
  }
  return cancellable
    .get(`/api/products?date=${dashed(date)}`, {
      cancelKey: 'getAllEvents'
    })
    .then(response => response && response.data)
}

export const getWeeklyCalendar = date => {
  if (!date) return {}
  return cancellable
    .get(`/api/calendar/weekly/${dashed(date)}`, {
      cancelKey: 'getWeeklyCalendar'
    })
    .then(response => response?.data)
}

export const getDailyCalendar = date => {
  if (!date) return {}
  return cancellable
    .get(`/api/calendar/daily/${dashed(date)}`, {
      cancelKey: 'getDailyCalendar'
    })
    .then(response => response && response.data)
}

export const getGppDailyCalendar = date => {
  if (!date) return {}
  return cancellable
    .get(`/api/calendar/gpp_daily/${dashed(date)}`, {
      cancelKey: 'getGppDailyCalendar'
    })
    .then(response => response && response.data)
}

export const getAddOns = (product, date, chosenEventZone = null) => {
  // SHEDDWEB-1070 - not all adddons are timed and only the timed ones need the datetime for the chosen base event, chosenEventZone
  if (!date || !product) return {}
  let formatedDate = dashed(date)

  if (chosenEventZone) {
    formatedDate = chosenEventZone.zoneTime
      ? formatedDate + 'T' + chosenEventZone.zoneTime
      : chosenEventZone
  }

  return safeApiCall(
    'get',
    `api/products/${product.productId}/addons?date=${formatedDate}`,
    "Sorry, we're having trouble finding this event's upgrade products"
  )
}

export const getCart = () => {
  return safeApiCall('get', '/cart', 'Could not find your cart.')
}

export const postEvent = (
  eventData,
  cartId,
  errorMessage = 'Sorry, those tickets are no longer available. Please try again.'
) => {
  const event = {
    nativeEventId: eventData.productId,
    performanceId: eventData.externalId || eventData.id,
    eventType: eventData.type,
    tickets: eventData.tickets.map(
      ({ priceTypeId, quantity, zoneId, priceTypeReasonId }) => ({
        priceTypeId,
        quantity,
        zoneId,
        priceTypeReasonId
      })
    )
  }

  return safeApiCall('post', '/items', errorMessage, {
    cartId,
    event
  })
}

export const updateItemReferrer = (abortSignal, referrerInfo) => {
  return abortableApiCall(
    'patch',
    abortSignal,
    `/items/${referrerInfo.itemId}`,
    { referrer_name: referrerInfo.referrerName }
  )
}

export const updateReferrerItems = (abortSignal, referrerInfos) => {
  referrerInfos.reduce(async (promiseToResolve, referrerInfo) => {
    await promiseToResolve
    return updateItemReferrer(abortSignal, referrerInfo)
  }, Promise.resolve())
}

export const postNote = note => {
  const errorMessage = 'There was an error saving the notes for your cart.'
  return safeApiCall('post', '/notes', errorMessage, note)
}

export const postNotes = notes => {
  notes.reduce(async (addLastNote, note) => {
    await addLastNote
    return postNote(note)
  }, Promise.resolve())
}

export const putPreferredRecognition = (abortSignal, text) => {
  return abortableApiCall('put', abortSignal, '/membership_items', text)
}

export const deleteItem = id => {
  return safeApiCall('delete', `/items/${id}`, 'Could not remove item.')
}

export const getDatesForEvent = async (id, startDate, group_size = null) => {
  const minimum_tickets = group_size ? group_size : 0
  const {
    disabledDates,
    // availableDates,
    noDatesAvailable,
    firstAvailableDate
  } = await safeApiCall(
    'get',
    `api/products/${id}/product_dates?start_date=${startDate}&min_tix=${minimum_tickets}`,
    "We're sorry! We couldn't find any availability for this product. Please make another selection."
  )

  const dateMapper = date => {
    // if this API call returns a date with a time less than the offset of the user's time zone
    // (e.g. if the returned time is 2020-12-20T00:06:00 and the user is in Pacific Standard Time (-8))
    // the date returned to the calendar will be rolled back one day

    // because we only care about the date, we can use luxon to preserve the UTC date and pass the correct
    // year, month and day to the native Date constructor, which will then have the correct date and a time of 00:00:00
    const luxonDate = DateTime.fromISO(date).toUTC()
    return new Date(luxonDate.year, luxonDate.month - 1, luxonDate.day)
    // luxonDate.month - 1 because JS dates use an array of months, so "0" is January :^)
  }

  return {
    disabledDates: disabledDates.map(dateMapper),
    noDatesAvailable,
    firstAvailableDate
  }
}

export const getAddon = async (id, date) => {
  const { events, _ } = await safeApiCall(
    'get',
    `api/addons/${id}?date=${date}`,
    "We couldn't find that upgrade."
  )
  const event = events.find(event => event.productId === id)
  if (!event) {
    console.warn(`Could not find native product with id ${id} on date ${date}`)
    throw 'There was an error trying to find this event'
  }
  return event
}

export const getProduct = async (id, date) => {
  const { events, _ } = await safeApiCall(
    'get',
    `api/products/${id}?date=${date}`,
    "We couldn't find that event."
  )
  const event = events.find(event => event.productId === id)
  if (!event) {
    console.warn(`Could not find native product with id ${id} on date ${date}`)
    throw 'There was an error trying to find this event'
  }
  return event
}

export const getRemainingTickets = async (
  performanceId,
  native_event_id = null,
  price_type_ids = []
) => {
  let endpoint = `api/available_tickets?performance_id=${performanceId}`
  if (native_event_id) {
    endpoint =
      endpoint +
      `&native_event_id=${native_event_id}&price_type_ids=${price_type_ids}`
  }
  return safeApiCall('get', endpoint, '')
}

export const getNativeEvent = async (nativeEventId, date, type) => {
  let event = null
  if (type.includes('addon')) {
    event = await getAddon(nativeEventId, date)
  } else {
    event = await getProduct(nativeEventId, date)
  }
  if (event.times.length !== 0) {
    event = event.times.find(time => time.occursAt === date)
  }
  return event
}

export const postItemsToCart = async (items, cartId) => {
  return await items.reduce(async (addLastItem, item) => {
    await addLastItem
    return postEvent(item, cartId)
  }, Promise.resolve())
}

export const deleteItemsFromCart = async items => {
  return await items.reduce(async (deletePreviousItem, item) => {
    await deletePreviousItem
    return deleteItem(item.id)
  }, Promise.resolve())
}

export const applyPromoCode = async code => {
  return safeApiCall(
    'get',
    `cart/apply_promo_code?promo_code=${code}`,
    'Invalid promo code.'
  )
}

export const removePromoCode = async () => {
  return safeApiCall(
    'get',
    `cart/remove_promo_code`,
    'Could not remove promo code.'
  )
}

export const getOrderHistory = async () => {
  return safeApiCall(
    'get',
    'api/orders_history',
    'Could not find your order history.'
  )
}

export const getOrderDetails = async order_no => {
  return safeApiCall(
    'get',
    `api/orders_history/${order_no}`,
    'Could not find order details.'
  )
}

// GPP APIs starting from here
export const getGroupList = async constituentId => {
  return safeApiCall(
    'get',
    `api/group_list?constituency=${constituentId}`,
    'Could not get group list.'
  )
}

export const getCollegeDaycareNonschoolsList = async () => {
  return safeApiCall(
    'get',
    `api/college_daycare_nonschools_list`,
    'Could not get College, Daycare or Non-schools list.'
  )
}

export const getStatesList = async countryId => {
  return safeApiCall(
    'get',
    `api/states_from_country_id?country_id=${countryId}`,
    'Could not get states'
  )
}

export const getFirstAvailableGppPriceTypesForAdmission = async () => {
  return safeApiCall(
    'get',
    `api/gpp_price_types_for_first_available_performance`,
    'Oops! Something went wrong. Please refresh this page and try again.'
  )
}

export const getFirstAvailableGppPriceTypesForOffsite = async programId => {
  return safeApiCall(
    'get',
    `api/gpp_offsite_price_types_for_first_available_program?program_id=${programId}`,
    'Oops! Something went wrong. Please refresh this page and try again.'
  )
}

export const getAllPublishedLearningPrograms = () => {
  return safeApiCall(
    'get',
    `api/get_all_published_learning_programs`,
    'Could not get Learning Programs'
  )
}

export const getPublishedLearningProgramById = async programId => {
  return safeApiCall(
    'get',
    `api/get_published_learning_program_by_id?program_id=${programId}`,
    `Could not get Learning Program by id ${programId}`
  )
}

export const getDatesForGroupSelections = async (abortSignal, queryParams) => {
  return abortableApiCall(
    'post',
    abortSignal,
    `/api/get_dates_for_group_selections`,
    queryParams
  )
}

export const getAllGrades = () => {
  return safeApiCall('get', `api/get_all_grades`, 'Could not get grades')
}

// chosenEventZone is the Shedd entry time for group trips
export const getAllAddOnsForGA = async (
  abortSignal,
  date,
  category = 'general'
) => {
  if (!date) return {}
  const { data } = await api.get(
    `api/get_all_addons?date=${date}&category=${category}`,
    {
      signal: abortSignal
    }
  )
  return data
}

export const getGroupTrip = () => {
  return safeApiCall('get', '/group_trips', 'could not find trip')
}

export const updateGroupTrip = async (abortSignal, groupTripId, body) => {
  return abortableApiCall(
    'put',
    abortSignal,
    `/group_trips/${groupTripId}`,
    body
  )
}

/*
 * Adds items to the group_itinerary_items and group_itinerary_reservations table for the trip
 * */
export const addItemsToItinerary = async (abortSignal, groupTripId, body) => {
  return abortableApiCall(
    'post',
    abortSignal,
    `/group_trips/${groupTripId}/add_itinerary_items`,
    body
  )
}

/*
 * Deletes group_itinerary_items and associated group_itinerary_reservations from a trip based on the "type"
 * If "type" == "" (ie: empty) ALL items are deleted from the trip (used when user goes back to calendar screen)
 * type == "programs" -- Deletes programs AND Lunches and Add Ons and scheduled Add Ons
 * type == "addOns" -- Deletes Add Ons Only.
 * */
export const deleteItineraryItemsFromTripByType = (
  groupTripId,
  typeOfItems
) => {
  let url = `/group_trips/${groupTripId}/delete_itinerary_items`
  if (typeOfItems) {
    url = url + `?eventType=${typeOfItems}`
  }
  return safeApiCall('delete', url, 'Could not remove items.')
}

/*
 * Makes the call to reserve the current items in Tessitura.
 * */
export const bookTrip = async (abortSignal, groupTripId) => {
  return abortableApiCall('post', abortSignal, `/group_trips/book_trip`, {
    id: groupTripId
  })
}

/*
 * Takes the group trip id, and a list of the group itinerary item ids and makes a delete call
 * to the API to remove them from the group trip.
 * */
export const removeItemsFromItineraryById = (groupTripId, idsToRemove) => {
  let queryString = `itemIds[]=${idsToRemove[0]}`

  if (idsToRemove.length > 1) {
    for (var i = 1; i < idsToRemove.length; i++) {
      queryString += `&itemIds[]=${idsToRemove[i]}`
    }
  }

  let url = `/group_trips/${groupTripId}/bulk_delete_items?${queryString}`

  return safeApiCall('delete', url, 'Could not remove items.')
}

/*
 * At a minimum, this call will dump the Tessitura cart associated with the group trip.
 * Passing in true for fullReset will also clear out the itineary items.
 * If the user navigates "Back" from the cart screen, fullReset will be false which will just empty the cart
 * in Tessitura but keep the group trip itineary items data.
 * If the user encounters an error on the cart, and they choose to return to calendar, pass in true to have
 * the itineary items be purged as well.
 * The hadExpired boolean changes based on if the cart timer initiated the call or not.
 * */
export const clearGroupCart = (groupTripId, fullReset, hadExpired = false) => {
  let url = `/group_trips/${groupTripId}/clear_cart?reset=${fullReset}&had_expired=${hadExpired}`

  return safeApiCall('delete', url, 'Could not reset trip.')
}

export const resetClearedCartFlag = groupTripId => {
  let url = `/group_trips/${groupTripId}/reset_cleared_cart_flag`
  return safeApiCall('post', url, 'Could not reset cleared cart flag.')
}

export const resetTrip = groupTripId => {
  let url = `/group_trips/${groupTripId}/reset`
  return safeApiCall('delete', url, 'Could not hard reset trip.')
}

export const getLunchAvailability = (studentSize, startDate) => {
  return safeApiCall(
    'get',
    `api/get_lunch_availability?student_size=${studentSize}&start_date=${startDate}`,
    'Could not get lunch availability'
  )
}

export const getResidentZipCodes = (abortSignal, zipCode) => {
  return abortableApiCall(
    'get',
    abortSignal,
    `api/get_resident_zip_codes?zip_code=${zipCode}`
  )
}
